import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import { Icon } from '@components/index';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import {
  INPUT_MAX_LENGTH,
  INPUT_MIN_LENGTH,
  MAX_LEAD_STATUS_COUNT
} from '@features/lead-management/constants/common';
import {
  useLazyGetStatusQuery,
  useUpdateStatusMutation
} from '@features/lead-management/queries/LeadManagementQuery';
import useNotify from '@hooks/notify';
import { ApiError } from '@types';
import { getLocalStorageItem } from '@utils/storage';
import { StatusEntity } from 'types/lead-management';

import DraggableStatusItem from './DraggableStatusItem';
import { isStatusData, LeadStatusItem } from './status-data';

import theme from '../../../../../themes/colors';

interface DraggableStatusListProps {
  statuses?: LeadStatusItem[];
  onStatusesChange?: (statuses: LeadStatusItem[]) => void;
  onRefresh?: () => void;
}

const DraggableStatusList = ({
  statuses: initialStatuses,
  onStatusesChange,
  onRefresh
}: DraggableStatusListProps) => {
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const { currentOrg } = useSelector((state: any) => state.user);

  const [statuses, setStatuses] = useState<LeadStatusItem[]>(
    initialStatuses || []
  );
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newStatusValue, setNewStatusValue] = useState('');
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const [updateStatus, { isLoading: isUpdating }] = useUpdateStatusMutation();
  const [getStatus] = useLazyGetStatusQuery();

  const fetchAndUpdateStatuses = async () => {
    if (!organizationId) return;
    const res = await getStatus({ organizationId });
    if (res.data && Array.isArray(res.data)) {
      const totalCount = res.data.length;
      const updatedStatuses: LeadStatusItem[] = res.data.map(
        (status: StatusEntity, index: number) => ({
          ...status,
          isLocked: index === 0 || index >= totalCount - 2
        })
      );
      setStatuses(updatedStatuses);
      onStatusesChange?.(updatedStatuses);
    }
  };

  const convertToStatusEntity = (items: LeadStatusItem[]): StatusEntity[] =>
    items.map((item) => ({
      id: item.id,
      value: item.value,
      order_key: item.order_key,
      is_custom: item.is_custom,
      color: item.color || '#3B82F6'
    }));

  const callUpdateStatusAPI = async (
    updatedStatuses: LeadStatusItem[]
  ): Promise<boolean> => {
    if (!organizationId) return false;

    const res = await updateStatus({
      organizationId,
      statuses: convertToStatusEntity(updatedStatuses)
    });

    if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(
        error?.data?.errors?.[0]?.message ||
          error?.data?.message ||
          'Failed to update status'
      );
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (initialStatuses) {
      setStatuses(initialStatuses);
    }
  }, [initialStatuses]);

  useEffect(() => {
    if (isAddingNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddingNew]);

  useEffect(
    () =>
      monitorForElements({
        canMonitor({ source }) {
          return isStatusData(source.data);
        },
        onDrop({ source, location }) {
          const target = location.current.dropTargets[0];
          if (!target) return;

          const sourceData = source.data;
          const targetData = target.data;

          if (!isStatusData(sourceData) || !isStatusData(targetData)) return;

          const sourceId = sourceData.statusId;
          const targetId = targetData.statusId;

          if (sourceId === targetId) return;

          const closestEdge = extractClosestEdge(targetData);

          setStatuses((currentStatuses) => {
            const sourceIndex = currentStatuses.findIndex(
              (s) => s.id === sourceId
            );
            const targetIndex = currentStatuses.findIndex(
              (s) => s.id === targetId
            );

            if (sourceIndex === -1 || targetIndex === -1)
              return currentStatuses;

            const sourceStatus = currentStatuses[sourceIndex];
            const targetStatus = currentStatuses[targetIndex];

            if (sourceStatus.isLocked || targetStatus.isLocked) {
              return currentStatuses;
            }

            const newStatuses = [...currentStatuses];
            newStatuses.splice(sourceIndex, 1);

            let insertIndex = targetIndex;
            if (sourceIndex < targetIndex) {
              insertIndex = targetIndex - 1;
            }

            if (closestEdge === 'bottom') {
              insertIndex += 1;
            }

            const firstLockedIndex = 0;
            const lastLockedStartIndex = newStatuses.length - 1;

            if (insertIndex <= firstLockedIndex) {
              insertIndex = firstLockedIndex + 1;
            }
            if (insertIndex > lastLockedStartIndex) {
              insertIndex = lastLockedStartIndex;
            }

            newStatuses.splice(insertIndex, 0, sourceStatus);

            const updatedStatuses = newStatuses.map((status, index) => ({
              ...status,
              order_key: index
            }));

            onStatusesChange?.(updatedStatuses);
            callUpdateStatusAPI(updatedStatuses);
            // Show success alert after reorder
            setTimeout(() => {
              createSuccessAlert('Lead status order updated successfully');
            }, 0);
            return updatedStatuses;
          });
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onStatusesChange, organizationId, createSuccessAlert]
  );

  const handleSaveStatus = async (
    status: LeadStatusItem,
    newValue: string
  ): Promise<boolean> => {
    const previousStatuses = [...statuses];
    const updatedStatuses = statuses.map((s) =>
      s.id === status.id ? { ...s, value: newValue } : s
    );
    setStatuses(updatedStatuses);
    setEditingStatusId(null);
    onStatusesChange?.(updatedStatuses);
    const success = await callUpdateStatusAPI(updatedStatuses);
    if (success) {
      sendMixpanelEvent(Event.LEAD_STATUS_UPDATE, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_status_count: updatedStatuses.length,
        lead_status: status.value,
        updated_status: newValue
      });
    } else {
      setStatuses(previousStatuses);
      onStatusesChange?.(previousStatuses);
    }
    return success;
  };

  const handleDeleteStatus = (
    status: LeadStatusItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    replacementStatusId: string
  ) => {
    // TODO: Call API to move leads from status.id to replacementStatusId
    // before deleting the status
    const updatedStatuses = statuses
      .filter((s) => s.id !== status.id)
      .map((s, i) => ({ ...s, order_key: i }));
    setStatuses(updatedStatuses);
    onStatusesChange?.(updatedStatuses);
    callUpdateStatusAPI(updatedStatuses);
    sendMixpanelEvent(Event.LEAD_STATUS_DELETION, {
      org_id: organizationId,
      org_name: currentOrg?.name,
      lead_status_count: updatedStatuses.length,
      deleted_lead_status: status.value
    });
    createSuccessAlert('Lead status deleted successfully');
  };

  const handleAddNewStatus = async () => {
    if (!newStatusValue.trim()) {
      setIsAddingNew(false);
      return;
    }

    // Check max status count
    if (statuses.length >= MAX_LEAD_STATUS_COUNT) {
      createErrorAlert(
        `Maximum ${MAX_LEAD_STATUS_COUNT} statuses allowed. Please delete an existing status to add a new one.`
      );
      setNewStatusValue('');
      setIsAddingNew(false);
      return;
    }

    const lockedEndStatuses = statuses.filter(
      (s) => s.isLocked && s.order_key >= statuses.length - 2
    );
    const nonLockedEndStatuses = statuses.filter(
      (s) => !s.isLocked || s.order_key < statuses.length - 2
    );

    const newStatus: LeadStatusItem = {
      id: '',
      value: newStatusValue.trim(),
      order_key: nonLockedEndStatuses.length,
      is_custom: true,
      isLocked: false
    };

    const updatedStatuses = [
      ...nonLockedEndStatuses,
      newStatus,
      ...lockedEndStatuses.map((s, i) => ({
        ...s,
        order_key: nonLockedEndStatuses.length + 1 + i
      }))
    ];

    // Optimistically show the new status
    setStatuses(updatedStatuses);
    onStatusesChange?.(updatedStatuses);

    const success = await callUpdateStatusAPI(updatedStatuses);
    if (success) {
      sendMixpanelEvent(Event.LEAD_STATUS_ADDITION, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_status_count: updatedStatuses.length,
        new_lead_status: newStatusValue.trim()
      });
      createSuccessAlert('Lead status created successfully');
      await fetchAndUpdateStatuses();
      onRefresh?.();
    } else if (initialStatuses) {
      // Revert on error - let initialStatuses take over
      setStatuses(initialStatuses);
      onStatusesChange?.(initialStatuses);
    }
    setNewStatusValue('');
    setIsAddingNew(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddNewStatus();
    } else if (e.key === 'Escape') {
      setNewStatusValue('');
      setIsAddingNew(false);
    }
  };

  const handleCancelAddStatus = () => {
    setNewStatusValue('');
    setIsAddingNew(false);
  };

  const isMaxStatusesReached = statuses.length >= MAX_LEAD_STATUS_COUNT;

  const startAddingStatus = () => {
    if (isMaxStatusesReached) {
      createErrorAlert(
        `Maximum ${MAX_LEAD_STATUS_COUNT} statuses allowed. Please delete an existing status to add a new one.`
      );
      return;
    }
    setIsAddingNew(true);
    setNewStatusValue('');
  };

  const lockedEndIndex = statuses.length - 2;
  const beforeLockedEnd = statuses.slice(0, lockedEndIndex);
  const lockedEnd = statuses.slice(lockedEndIndex);

  return {
    statuses,
    startAddingStatus,
    isAddingNew,
    isUpdating,
    isMaxStatusesReached,
    StatusList: (
      <div className="flex flex-col gap-2">
        {beforeLockedEnd.map((status) => (
          <DraggableStatusItem
            key={status.id}
            status={status}
            allStatuses={statuses}
            onSave={handleSaveStatus}
            onDelete={handleDeleteStatus}
            isEditing={editingStatusId === status.id}
            onEditStart={() => setEditingStatusId(status.id)}
            onEditEnd={() => setEditingStatusId(null)}
          />
        ))}
        {isAddingNew && (
          <div className="flex max-w-139 items-center justify-between rounded-lg border border-grey40 bg-white px-4 py-3">
            <div className="flex flex-1 items-center gap-3">
              <Icon
                name="reorder-handle"
                size="none"
                className="size-4 text-disabledContent opacity-50"
              />
              <input
                ref={inputRef}
                type="text"
                value={newStatusValue}
                onChange={(e) => setNewStatusValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleCancelAddStatus}
                className="mr-2 flex-1 rounded-lg border border-primary bg-transparent px-4 py-2.5 text-sm font-medium text-content outline-none"
                placeholder="Enter status name"
                maxLength={INPUT_MAX_LENGTH}
                minLength={INPUT_MIN_LENGTH}
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleAddNewStatus}
                className="rounded p-1 transition-opacity hover:bg-grey20"
                aria-label="Save"
              >
                <Icon
                  name="check-icon-green"
                  size="none"
                  className="size-3 text-primary"
                  stroke={theme.primary}
                />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCancelAddStatus}
                className="rounded p-1 transition-opacity hover:bg-grey20"
                aria-label="Cancel"
              >
                <Icon
                  name="close-icon-red"
                  size="none"
                  className="size-2.5"
                  stroke={theme.error}
                />
              </button>
            </div>
          </div>
        )}
        {lockedEnd.map((status) => (
          <DraggableStatusItem
            key={status.id}
            status={status}
            allStatuses={statuses}
            onSave={handleSaveStatus}
            onDelete={handleDeleteStatus}
            isEditing={editingStatusId === status.id}
            onEditStart={() => setEditingStatusId(status.id)}
            onEditEnd={() => setEditingStatusId(null)}
          />
        ))}
      </div>
    )
  };
};

export default DraggableStatusList;
